import { useMemo, useState } from 'react';

import clsx from 'clsx';

import { useLiveReading } from '../../../contexts/LiveReadingContext';
import { DeviceStatusPopover } from './DeviceStatusPopover';
import { IAppliance } from '../../../types/Appliance/Appliance';
import usePlanetDevice from '../../../data/planetDevice/usePlanetDevice';
import { atmoVersionDataPoint } from '../../../constants/datapointLists';

interface DeviceStatusBarProps {
  appliance: IAppliance;
}

export function DeviceStatusBar({ appliance }: DeviceStatusBarProps) {
  const { planetDevice } = usePlanetDevice(appliance.deviceId ?? '', true);
  const { latest, loading } = useLiveReading();
  const [open, setOpen] = useState(false);

  const state = useMemo(() => {
    if (
      !planetDevice.isLoading &&
      planetDevice.data &&
      planetDevice.data.length &&
      !loading &&
      latest
    ) {
      return planetDevice.data![0].gateway.device.cloud.connected
        ? latest!.error.code > 0
          ? 'Error'
          : 'Online'
        : 'Offline';
    }
  }, [planetDevice.isLoading, planetDevice.data, latest, loading]);

  const gatewayVersion = useMemo(() => {
    if (
      !planetDevice.isLoading &&
      planetDevice.data &&
      planetDevice.data.length
    ) {
      return planetDevice.data![0].gateway?.device.version.software;
    }
  }, [planetDevice.isLoading, planetDevice.data]);

  const coProcVersion = useMemo(
    () =>
      latest?.readingData
        .find((rd) => rd.dataPointId === atmoVersionDataPoint)
        ?.transformedValue.toString() ?? '0.0',
    [latest]
  );

  if (
    planetDevice.isLoading ||
    !planetDevice.data ||
    !planetDevice.data.length ||
    loading ||
    !latest ||
    latest.error === null ||
    state === undefined
  ) {
    return <></>;
  } else {
    return (
      <div className="relative z-0">
        <div className="flex flex-row justify-end md:justify-start items-center space-x-4">
          {gatewayVersion === '00.01.00' ? (
            state === 'Error' ? (
              <>
                <p>{state}</p>
                <div
                  onMouseEnter={() => setOpen(true)}
                  onMouseLeave={() => setOpen(false)}
                  className={clsx(
                    'w-6 h-6 rounded-full',
                    {
                      Online: 'bg-green-600',
                      Error: 'bg-amber-600',
                      Offline: 'bg-red-600',
                    }[state]
                  )}
                />
                <DeviceStatusPopover
                  open={open}
                  connected={
                    planetDevice.data![0].gateway.device.cloud.connected
                  }
                  error={latest.error}
                  deviceVersion={coProcVersion}
                />
              </>
            ) : (
              <></>
            )
          ) : (
            <>
              <p>{state}</p>
              <div
                onMouseEnter={() => setOpen(true)}
                onMouseLeave={() => setOpen(false)}
                className={clsx(
                  'w-6 h-6 rounded-full',
                  {
                    Online: 'bg-green-600',
                    Error: 'bg-amber-600',
                    Offline: 'bg-red-600',
                  }[state]
                )}
              />
              <DeviceStatusPopover
                open={open}
                connected={planetDevice.data![0].gateway.device.cloud.connected}
                error={latest.error}
                deviceVersion={coProcVersion}
              />
            </>
          )}
        </div>
      </div>
    );
  }
}
